// =====================================================================
// frontend/src/hooks/useModalFocusTrap.js
//
// Shared a11y plumbing for every modal/drawer in the app (canonical home —
// promoted from pages/alerts/useModalFocusTrap.js, which had the same
// logic scoped to just that folder's two modals). Wires the standard
// dialog focus contract:
//   - focus moves into the modal on open (first focusable element)
//   - Tab / Shift+Tab cycle within the modal's focusable elements only
//     (a real trap — Tab can't escape to the page behind it)
//   - Escape closes
//   - focus returns to whatever element triggered the modal, on close
//
// `onClose` is read through a ref rather than depended on directly so that
// callers passing an inline arrow function (a fresh identity every render)
// don't cause this effect to tear down and re-run — which would re-steal
// focus back into the modal on every unrelated re-render.
//
// `active` (default true) lets callers that keep their modal component
// permanently mounted (e.g. the shared <Modal>/<Drawer> wrappers, which
// stay in the tree so they can portal + animate) gate the trap to only
// engage while genuinely open, instead of relying on mount/unmount.
// =====================================================================

import { useEffect, useRef } from 'react'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

export default function useModalFocusTrap(containerRef, onClose, active = true) {
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose }, [onClose])

  useEffect(() => {
    if (!active) return
    const container = containerRef.current
    const previouslyFocused = document.activeElement

    const getFocusable = () =>
      Array.from(container?.querySelectorAll(FOCUSABLE_SELECTOR) || [])

    // Move focus into the modal on open. Falls back to the container
    // itself (which carries tabIndex={-1}) if nothing focusable exists.
    const firstFocusable = getFocusable()[0]
    if (firstFocusable) firstFocusable.focus()
    else container?.focus()

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onCloseRef.current?.()
        return
      }
      if (e.key !== 'Tab') return

      const items = getFocusable()
      if (items.length === 0) {
        e.preventDefault()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const activeEl = document.activeElement

      if (e.shiftKey) {
        if (activeEl === first || !container.contains(activeEl)) {
          e.preventDefault()
          last.focus()
        }
      } else if (activeEl === last || !container.contains(activeEl)) {
        e.preventDefault()
        first.focus()
      }
    }

    // Capture phase so this reliably wins even if a child stops
    // propagation on the bubble phase.
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      // Restore focus to the trigger. Guard against the trigger itself
      // having unmounted while the modal was open.
      if (previouslyFocused && previouslyFocused.isConnected) {
        previouslyFocused.focus()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setup/teardown
    // is tied to this modal instance's open/close transition only; see
    // onCloseRef comment above for why onClose itself isn't a dependency.
  }, [containerRef, active])
}
